// On the device only, so teammates sharing a store never change each other's. index.html's
// inline script repeats these rules before React loads: keep the key and paths in step.
export const THEME_STORAGE_KEY = 'dashboard-theme';
export const THEMES = ['light', 'dark', 'system'];
// Stores the year it was turned on, so it switches itself off once Halloween is over.
export const HALLOWEEN_STORAGE_KEY = 'dashboard-halloween';
const DEFAULT_THEME = 'system';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set();
let scopes = 0;

export function getThemePreference() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return THEMES.includes(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function isHalloweenSeason(now = new Date()) {
  return now.getMonth() === 9 && now.getDate() >= 25;
}

export function isHalloweenOn(now = new Date()) {
  try {
    const stored = window.localStorage.getItem(HALLOWEEN_STORAGE_KEY);
    return isHalloweenSeason(now) && stored === String(now.getFullYear());
  } catch {
    return false;
  }
}

/** The menu's choice: the Halloween theme sits over Light / Dark / System without replacing it. */
export function getThemeChoice() {
  return isHalloweenOn() ? 'halloween' : getThemePreference();
}

function systemPrefersDark() {
  return Boolean(window.matchMedia?.(DARK_QUERY).matches);
}

export function getResolvedTheme() {
  if (isHalloweenOn()) return 'dark';
  const preference = getThemePreference();
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

/** What is on screen: signed-out screens never enter the scope, so they stay light. */
export function getAppliedTheme() {
  return scopes > 0 ? getResolvedTheme() : 'light';
}

function applyTheme() {
  const dark = getAppliedTheme() === 'dark';
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  root.classList.toggle('halloween', scopes > 0 && isHalloweenOn());
  // Native controls (scrollbars, date inputs) follow; light keeps the browser default.
  root.style.colorScheme = dark ? 'dark' : '';
}

function notify() {
  applyTheme();
  listeners.forEach(listener => listener());
}

export function setThemePreference(theme) {
  try {
    if (theme === 'halloween') {
      window.localStorage.setItem(HALLOWEEN_STORAGE_KEY, String(new Date().getFullYear()));
    } else {
      window.localStorage.removeItem(HALLOWEEN_STORAGE_KEY);
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  } catch {
    // Storage blocked: the choice still applies until the tab closes.
  }
  notify();
}

const onStorage = event =>
  [THEME_STORAGE_KEY, HALLOWEEN_STORAGE_KEY].includes(event.key) && notify();

let media;

export function subscribeToTheme(listener) {
  if (listeners.size === 0) {
    media = window.matchMedia?.(DARK_QUERY);
    media?.addEventListener('change', notify);
    window.addEventListener('storage', onStorage);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      media?.removeEventListener('change', notify);
      window.removeEventListener('storage', onStorage);
    }
  };
}

export function enterThemeScope() {
  scopes += 1;
  notify();
  return () => {
    scopes -= 1;
    notify();
  };
}
